import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PALETTE = ["#d4af37", "#8b5cf6", "#22d3ee", "#ef4444", "#10b981", "#f97316", "#ec4899", "#3b82f6"];

export default function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState(PALETTE[0]);

  const load = async () => {
    const r = await api.get("/categories");
    setCats(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/categories", { name: name.trim(), color });
    setName(""); setColor(PALETTE[0]);
    toast.success("Category added");
    load();
  };
  const del = async (id) => {
    await api.delete(`/categories/${id}`);
    load();
  };
  const rename = async (id, newName) => {
    await api.put(`/categories/${id}`, { name: newName });
    load();
  };
  const recolor = async (id, newColor) => {
    await api.put(`/categories/${id}`, { color: newColor });
    load();
  };

  return (
    <div className="p-10 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Structure</div>
        <h1 className="font-heading font-light text-4xl tracking-tighter mt-2">Categories</h1>
      </div>

      <form onSubmit={add} className="border border-zinc-200 bg-white p-5 flex flex-wrap gap-3 items-center mb-8">
        <input
          data-testid="new-cat-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Trail Running Shoes"
          className="flex-1 min-w-[200px] text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm"
        />
        <div className="flex gap-1">
          {PALETTE.map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`palette-${c}`}
              onClick={() => setColor(c)}
              style={{ background: c }}
              className={`w-6 h-6 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-zinc-950 scale-110" : "hover:scale-110"}`}
              aria-label={c}
            />
          ))}
        </div>
        <button
          data-testid="add-cat-btn"
          type="submit"
          className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm px-4 py-2.5 hover:bg-zinc-800 rounded-sm"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </form>

      {cats.length === 0 ? (
        <div className="border border-dashed border-zinc-200 p-10 text-center text-sm text-zinc-500">No categories yet.</div>
      ) : (
        <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
          {cats.map((c) => (
            <div key={c.id} data-testid={`cat-row-${c.id}`} className="px-5 py-4 flex items-center gap-4">
              <button
                data-testid={`cat-color-${c.id}`}
                onClick={() => {
                  const idx = PALETTE.indexOf(c.color);
                  const next = PALETTE[(idx + 1) % PALETTE.length];
                  recolor(c.id, next);
                }}
                style={{ background: c.color }}
                className="w-8 h-8 rounded-full shrink-0 hover:scale-110 transition-transform"
                aria-label="Change color"
              />
              <input
                data-testid={`cat-name-${c.id}`}
                defaultValue={c.name}
                onBlur={(e) => e.target.value !== c.name && rename(c.id, e.target.value)}
                className="flex-1 font-medium bg-transparent border-0 outline-none focus:bg-white focus:ring-1 focus:ring-zinc-300 px-2 py-1 rounded-sm"
              />
              <button
                data-testid={`cat-delete-${c.id}`}
                onClick={() => del(c.id)}
                className="text-zinc-400 hover:text-red-500 p-1.5"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
